import {LitElement, html, css} from 'lit';
import {connect} from 'pwa-helpers';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import SunCalc from 'suncalc';
import {getFuzzyLocalTimeFromPoint} from '@mapbox/timespace';
import {msg} from '@lit/localize';
import store from '../../../store';
import {lightProfiles} from '../../../lightProfiles';
import {selectColorTemperature, selectProfileId} from '../../../selectors';
import {setChannelId} from '../../../slices/lightProfileSlice';
import {cssProperties} from '../../../css/cssProperties';
import {pimText} from '../../../util/values';

@customElement('param-table')
export class ParamTable extends connect(store)(LitElement) {

	static styles = [
		css`
			a {
				color: #000;
			}

			.paramTable {
				border-collapse: collapse;
				width: 96vw;
				margin: 0;
				margin-bottom: 30px;
			}

			.paramTable th {
				font: var(--font-specsheet-print-light);
				line-height: 10.5px;
				padding: 5px 10px;
				padding-left: 0;
				text-align: left;
				vertical-align: top;
			}

			.paramTable th span {
				font: var(--font-specsheet-print-bold);
			}

			.paramTable tr {
				border-bottom: 1px solid #C6C6C6;
				display: table-row-group;
				padding-left: 0;
				min-width: 125px;
				vertical-align: top;
			}

			.paramTable td {
				padding: 5px 10px;
				padding-left: 0;
				width: 25vw;
				padding-top: 10px;
				padding-bottom: 10px;
			}

			.tableHeader {
				display: table;
			}

			thead {
				height: 20px;
			}

			h1 {
				font: var(--font-specsheet-print-light);
				margin: 0;
			}

			h1 span {
				font: var(--font-specsheet-print-bold);
			}

			p.param {
				font: var(--font-specsheet-print-light);
			}

			p.paramValue {
				font: var(--font-specsheet-print-light);
				margin-top: 5px;
				margin-bottom: 5px;
			}

			tr.noBorder {
				border: none;
			}

			th.noPadding {
				width: 22.5vw;
			}
        `];

	static properties = {
		fixedDateAttr: {type: Date},
	};

	renderChannels = [];

	constructor(props) {
		super(props);
		store.subscribe(() => this.requestUpdate());

		// getting fixed date from attribute set in parent component
		const fixedDateAttr = this.getAttribute('fixed-date') || '';
		this.fixedDate = new Date(fixedDateAttr);
		// creating table rows based on different examplary times
		this.rows = [{
			time: '00:00:00',
		}, {
			time: '04:00:00',
		}, {
			time: '08:00:00',
		}, {
			time: '12:00:00',
		}, {
			time: '16:00:00',
		}, {
			time: '20:00:00',
		}];
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.decisions = state.decisions.data;
		this.profile = state.lightProfile.data.profile;
		this.lightProfile = state.lightProfile.data;
		this.location = state.lightProfile.data.project.location;
		this.lang = state.status.lang;
		this.userProfile = state.userProfile.data;
		const profileId = selectProfileId(state);
		if (profileId) {
			const lightProfile = lightProfiles.find(lp => lp.id === profileId);
			this.renderChannels = lightProfile?.renderChannels;
			this.layers = lightProfile?.layers;
		}
	}

	setTarget(renderChannelId) {
		store.dispatch(setChannelId({
			channelId: renderChannelId
		}));
	}

	// convert "10:00 AM" to number of seconds of day to use in calculations
	getTimeInSeconds(time) {
		const timeParts = time.split(':');
		const hours = parseInt(timeParts[0], 10);
		const minutes = parseInt(timeParts[1], 10);
		return hours * 3600 + minutes * 60;
	}

	// getKeyframe with fixed day-dayOfYear and timeInSeconds instead of dynamic values on the interactive configurator - selectedDay and selectedSecond
	getKeyframe(keyframes, dayOfYear, timeInSeconds) {
		const timezone = getFuzzyLocalTimeFromPoint(Date.now(), [this.location.longitude, this.location.latitude]);
		const myOffset = new Date().getTimezoneOffset();
		const offset = timezone._offset + myOffset;

		const date = new Date(2024, 0, dayOfYear, 0, 0, timeInSeconds - offset * 60);
		const localdatestring = date.toLocaleString('en-US', {dateStyle: 'medium', timeStyle: 'medium'});
		const locald = new Date(Date.parse(localdatestring));
		const timestamp = locald.getTime();
		const keyframe = keyframes.find(kf => kf[0] * 1000 > timestamp && kf[0] * 1000 < timestamp + 3600 * 12 * 1000 && kf[0] * 1000 > timestamp - 3600 * 12 * 1000);
		// find the element before keyframe in the array
		const index = keyframes.indexOf(keyframe);
		return keyframes[index - 1];
	}

	// calculating intensity values for each render channel with fixed values, channels are depending on the profile chosen, there can be 2 or 1 channel, each can contain 1 or 2 layers
	getIntensityValue(renderChannel, dayOfYear, timeInSeconds) {
		//console.log( 'getIntensityValue:', renderChannel, dayOfYear, timeInSeconds);
		let displayIntensity = 0;
		if (renderChannel) {
			renderChannel.layers.forEach(layer => {
				const keyframe = this.getKeyframe(this.lightProfile.simulationResult.targets[layer].keyframes, dayOfYear, timeInSeconds);
				const inten = keyframe ? (keyframe[2] * 100) : 0;
				displayIntensity = Math.max(inten, displayIntensity);
			});
		}
		return displayIntensity;
	}

	// calculating temperature values for each render channel with fixed values, channels are depending on the profile chosen, there can be 2 or 1 channel, each can contain 1 or 2 layers
	getTemperatureValue(renderChannel, dayOfYear, timeInSeconds) {
		//console.log( 'getTemperatureValue:', renderChannel, dayOfYear, timeInSeconds);
		let displayTemperature = '---';
		let displayIntensity = 0;
		if (renderChannel) {
			renderChannel.layers.forEach(layer => {
				const keyframe = this.getKeyframe(this.lightProfile.simulationResult.targets[layer].keyframes, dayOfYear, timeInSeconds);
				let ctt = 0;
				if ( keyframe && keyframe.length === 4) {
					ctt = this.getTemperature(keyframe[3]);
				} else {
					if (this.lightProfile.project.base === 'AdjustingWhite' ||
						this.lightProfile.project.base === 'DynamicAmbiAnce' ||
						this.lightProfile.project.base === 'DynamicPath') {
						const colorTemperatureFromLamp = selectColorTemperature(this.state) ?? 'standard';
						ctt = selectColorTemperature(this.state) ?? 'standard';
					} else {
						ctt = this.layers[layer].ctt;
					}
				}
				const inten = keyframe ? (keyframe[2] * 100) : 0;
				displayIntensity = Math.max(inten, displayIntensity);
				displayTemperature = inten > 0 ? ctt : displayTemperature;
			});
		}
		return displayTemperature;
	}

	getIntensity(intensity) {
		if (intensity === 0) {
			return '---';
		} else if (intensity < 33) {
			return msg('low');
		} else if (intensity < 66) {
			return msg('medium');
		} else {
			return msg('high');
		}
	}

	getTemperature(temperature) {
		if (temperature === 0 || temperature === '0') {
			return '---';
		} else if (temperature === 'red') {
			return msg('red');
		} else if (temperature <= 2400 || temperature <= '2400K') {
			return msg('warm');
		} else if (temperature <= 2900 || temperature <= '2900K') {
			return msg('normal');
		} else {
			return msg('cold');
		}
	}

	render() {
		if (!this.lightProfile.simulationResult) return html``;
		// SUNCALC for sunrise/sunset based on fixed date chosen and location chosen in the configurator
		const startOfYear = new Date(this.fixedDate.getFullYear(), 0, 1);
		const millisecondsInDay = 24 * 60 * 60 * 1000;
		const dayOfYear = Math.floor((this.fixedDate - startOfYear) / millisecondsInDay) + 1;
		const latitude = this.location.latitude;
		const longitude = this.location.longitude;

		// const date = new Date(2024, 0, this.selectedDay, 12, 0, this.selectedSecond);
		const suntime = SunCalc.getTimes(this.fixedDate, latitude, longitude);
		const sunriseTs = Date.parse(suntime.sunrise.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium', timeZone: this.location.timezoneName, hours12: false }));
		const sunsetTs = Date.parse(suntime.sunset.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium', timeZone: this.location.timezoneName, hours12: false }));
		const sunrise = new Date(sunriseTs).toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();
		const sunset = new Date(sunsetTs).toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();

		//const fixedSunriseTime = sunrise.toLocaleTimeString(this.lang, {hour: '2-digit', minute: '2-digit'});
		//const fixedSunsetTime = sunset.toLocaleTimeString(this.lang, {hour: '2-digit', minute: '2-digit'});
		const lightDistribution = pimText(this.lang, 'LE_LightDistribution_1', 'FV.S', this.state.decisions.data, this.state.productData.translations);
		const lightDistributionParts = lightDistribution.split(' + ');

		return html`

				<table class="paramTable">
					<thead>
					<tr class="noBorder">
						<th class="noPadding"><h1><span>${msg('Date')}</span>
							${this.fixedDate.toLocaleDateString(this.lang, {month: 'long', day: 'numeric', hours12: false})}</h1></th>
						<th><span>${msg('Sunrise')}</span> ${sunrise}<br>
							<span>${msg('Sunset')} </span>${sunset}
						</th>
						<th></th>
					</tr>
					<thead>
					<tbody>
					<tr>
						<th colspan="1">${msg('Time of the day')}</th>
						<th colspan="1">${msg('Light Distribution')}</th>
						<th colspan="1">${msg('Light intensity')}</th>
						<th colspan="1">${msg('Light color')}</th>
					</tr>
					<!-- renderChannels are depending on the profile chosen, there can be 2 or 1 channel, each can contain 1 or 2 layers
					each table column can have either one or two values - shift method is separating the first channel from the rest -->
					${this.rows.map((row) => html`
							<tr>
								<td>
									<p class="param">${row.time}</p>
								</td>
								<td colspan="1">
									<div class="paramContainer">
										<p class="paramValue">
											${repeat([...this.renderChannels],
												(renderChannel) => renderChannel.id,
												(renderChannel, index) => html`<div>${lightDistributionParts[index] ?? renderChannel.label}</div>`)
											}
										</p>
									</div>
								</td>
								<td colspan="1">
									<p class="paramValue">
										${repeat([...this.renderChannels],
											(renderChannel) => renderChannel.id,
											(renderChannel, index) => html`${this.getIntensity(this.getIntensityValue(renderChannel, dayOfYear, this.getTimeInSeconds(row.time)))}<br>`)
										}
									</p>
								</td>
								<td colspan="1">
									<p class="paramValue">
										${repeat([...this.renderChannels],
											(renderChannel) => renderChannel.id,
											(renderChannel, index) => html`${this.getTemperatureValue(renderChannel, dayOfYear, this.getTimeInSeconds(row.time))}<br>`)
										}
									</p>
								</td>
							</tr>
						`
					)}
					</tbody>
				</table>
`;
	}
}
